import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useApi } from '../hooks/useApi'
import { Nav } from '../components/Nav'
import { Loading } from '../components/Loading'
import type { Quote, DailyMetric } from '../types'

export default function Home() {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth()
  const { get } = useApi()

  const [quote, setQuote] = useState<Quote | null>(null)
  const [stats, setStats] = useState<{ sessions: number; avgDelta: number | null; streak: number } | null>(null)
  const [loading, setLoading] = useState(true)

  // Auto-login on first visit
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      login().catch(console.error)
    }
  }, [authLoading, isAuthenticated, login])

  // Load quote and stats
  useEffect(() => {
    if (!isAuthenticated) return

    const loadData = async () => {
      try {
        // Load quote
        const quoteData = await get<{ quote: Quote }>('/api/quotes')
        setQuote(quoteData.quote)

        // Load stats for last 7 days
        const today = new Date().toISOString().split('T')[0]
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        const metricsData = await get<{ metrics: DailyMetric[]; summary: { total_sessions: number; avg_intensity_delta: number | null } }>(
          `/api/metrics/daily?from=${weekAgo}&to=${today}`
        )

        // Calculate streak from metrics
        let streak = 0
        const sortedMetrics = [...metricsData.metrics].sort((a, b) =>
          new Date(b.day).getTime() - new Date(a.day).getTime()
        )
        for (const metric of sortedMetrics) {
          if (metric.sessions_count > 0) streak++
          else break
        }

        setStats({
          sessions: metricsData.summary.total_sessions,
          avgDelta: metricsData.summary.avg_intensity_delta,
          streak: streak || 0,
        })
      } catch (error) {
        console.error('Failed to load home data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isAuthenticated, get])

  const isNewUser = !loading && (!stats || stats.sessions === 0)
  const isReturningUser = !loading && stats && stats.sessions > 0

  if (authLoading) {
    return (
      <div className="page">
        <Loading message="Setting up..." />
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-icon">🔮</div>
          <h1 className="hero-title">PresentLens</h1>
          <p className="hero-subtitle">
            Transform anxious thoughts into balanced perspectives
          </p>
        </div>

        {/* Quote card - shown for returning users */}
        {isReturningUser && quote && (
          <div className="quote-card">
            <p className="quote-text">"{quote.text}"</p>
            <p className="quote-author">— {quote.author}</p>
          </div>
        )}

        {/* How it works - shown for new users */}
        {isNewUser && (
          <div className="how-it-works">
            <h2 className="section-title">How It Works</h2>
            <div className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <div className="step-title">Share</div>
                  <div className="step-desc">Speak or type what's on your mind</div>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <div className="step-title">Reframe</div>
                  <div className="step-desc">AI helps identify thinking patterns</div>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <div className="step-title">Grow</div>
                  <div className="step-desc">Track progress and build resilience</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats summary for returning users */}
        {isReturningUser && stats && (
          <div className="stats-summary">
            <div className="stat-item">
              <div className="stat-value">{stats.sessions}</div>
              <div className="stat-label">Sessions</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {stats.avgDelta !== null ? (
                  stats.avgDelta < 0 ? stats.avgDelta.toFixed(1) : `+${stats.avgDelta.toFixed(1)}`
                ) : '—'}
              </div>
              <div className="stat-label">Avg Change</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.streak}</div>
              <div className="stat-label">Day Streak</div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="cta-section">
          <Link to="/new">
            <button className="btn btn-record">
              <span style={{ fontSize: '1.5rem' }}>
                {isNewUser ? 'Try It Free' : 'Start Session'}
              </span>
            </button>
          </Link>
          <p className="cta-hint">
            {isNewUser
              ? 'No account needed • 100% private'
              : 'Tap to begin a quick reframe'
            }
          </p>
        </div>

        {/* Benefits for new users */}
        {isNewUser && (
          <div className="benefits-section">
            <div className="benefit">
              <span className="benefit-icon">🎙️</span>
              <span className="benefit-text">Voice-first design</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">🔒</span>
              <span className="benefit-text">Private & secure</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">⚡</span>
              <span className="benefit-text">2-minute sessions</span>
            </div>
          </div>
        )}

        {/* Quick access to history for returning users */}
        {isReturningUser && (
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <Link
              to="/history"
              style={{
                color: 'var(--color-primary)',
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
            >
              View {stats!.sessions} past session{stats!.sessions !== 1 ? 's' : ''} →
            </Link>
          </div>
        )}

        {/* Quote for new users at the bottom */}
        {isNewUser && quote && (
          <div className="quote-card" style={{ marginTop: '2rem' }}>
            <p className="quote-text">"{quote.text}"</p>
            <p className="quote-author">— {quote.author}</p>
          </div>
        )}

        {/* Crisis resources link */}
        <Link to="/crisis" className="crisis-link">
          Need support?
        </Link>
      </div>

      <Nav />
    </div>
  )
}
